import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, CheckCircle, XCircle, Clock } from 'lucide-react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  verification_pending: { label: 'Verification Pending', icon: Clock, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', desc: 'Your application is under review. Trusted members will verify your credentials.' },
  approved: { label: 'Approved', icon: CheckCircle, color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', desc: 'Your application has been approved. You will receive onboarding instructions shortly.' },
  rejected: { label: 'Not Approved', icon: XCircle, color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', desc: 'Your application was not approved at this time. Please see remarks below.' },
};

const TrackApplication = () => {
  const [appId, setAppId] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!appId.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/waitlist/track/${appId.trim().toUpperCase()}`);
      setResult(data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('No application found with this ID. Please check and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const cfg = result ? statusConfig[result.status] || statusConfig.verification_pending : null;

  return (
    <div className="min-h-screen bg-[#EDECEA]" data-testid="track-page">
      <Navigation />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-lg mx-auto px-6 md:px-12 pt-32 pb-20"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-[#9B9589] hover:text-[#0F1B2D] transition-colors mb-10" data-testid="track-back-link">
          <ArrowLeft size={16} />Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <div className="h-[2px] w-10 bg-[#C4A35A]" />
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#9B9589] font-bold">Application Status</p>
        </div>

        <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0F1B2D] tracking-tight mb-3">
          Track Your Application
        </h1>
        <p className="text-sm text-[#4A4640] font-medium leading-relaxed mb-8">
          Enter your Application ID to check the current status of your membership request.
        </p>

        <form onSubmit={handleTrack} className="flex gap-3 mb-8">
          <Input
            type="text"
            value={appId}
            onChange={(e) => setAppId(e.target.value.toUpperCase())}
            placeholder="AINAA-NEW-XXXXX-XX"
            className="input-ainaa h-12 flex-1 px-4 font-mono uppercase"
            data-testid="track-input"
          />
          <button type="submit" disabled={loading} className="btn-ainaa-primary px-6 h-12 text-sm font-semibold flex items-center gap-2 uppercase tracking-wider" data-testid="track-submit">
            <Search size={16} />
            {loading ? 'Searching...' : 'Track'}
          </button>
        </form>

        {error && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border-2 border-red-200 bg-red-50 p-5">
            <p className="text-sm text-red-700 font-medium" data-testid="track-error">{error}</p>
          </motion.div>
        )}

        {result && cfg && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="border-2 border-[#0F1B2D] bg-white" data-testid="track-result">
            {/* Application ID header */}
            <div className="bg-[#0F1B2D] px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-[9px] uppercase tracking-[0.2em] text-[#9B9589] font-bold">Application</p>
                <p className="font-mono text-lg font-bold text-[#F5F3EE]">{result.application_id}</p>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                <cfg.icon size={14} />
                <span className="text-xs font-bold uppercase tracking-wider">{cfg.label}</span>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold mb-1">Applicant</p>
                <p className="text-sm font-semibold text-[#0F1B2D]">{result.name}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold mb-1">Status</p>
                <p className="text-sm text-[#4A4640] font-medium leading-relaxed">{cfg.desc}</p>
              </div>
              {result.remarks && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-red-600 font-bold mb-1">Remarks</p>
                  <p className="text-sm text-red-700 font-medium leading-relaxed bg-red-50 p-3 border border-red-200">{result.remarks}</p>
                </div>
              )}
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#9B9589] font-bold mb-1">Applied On</p>
                <p className="text-sm text-[#4A4640] font-medium">{new Date(result.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      <Footer />
    </div>
  );
};

export default TrackApplication;
